import { ReactComponent as AlertIcon } from '../../assets/images/circle-exclaimation-icon.svg';
import { SUPPORTED_STACKS_SENTENCE } from './stackLogos';

interface NoAppDetectedNoticeProps {
  // The folder that was inspected, shown so the message is about a place and
  // not about the repository as a whole.
  rootDirectoryPath: string;
  onBrowseRepository: () => void;
  // With the buildpack route off, a Dockerfile is the only way in — listing the
  // stacks we build would be advertising something this environment cannot do.
  buildpacksAvailable?: boolean;
}

// The subfolders this repository DOES have an app in are not offered here.
//
// They are offered on the source step, in the drawer that asks for the branch
// and the folder, where Continue waits until one of them is chosen — so by the
// time anyone reads this notice they have already been past that list once.
// Repeating it on a screen whose own complaint is how much there is to read
// was a second answer to a question already answered, and the button below
// reopens the browser those paths came from.

// Shown when the detection found nothing to build in the selected folder.
//
// A warning, not a block: the detection only predicts what the builder will
// decide, so a false negative must not stop a deploy that would have worked.
// That is why it is a hairline and an icon rather than a filled red panel.
//
// The lead names the stacks instead of the files. The first question this
// state raises is "is my technology supported at all?", and a list of six
// filenames answers it badly — it reads like the whole of what we accept. The
// filenames still have to appear, because they are what the user can actually
// check, but they belong in the how, not in the headline.
const NoAppDetectedNotice = ({
  rootDirectoryPath,
  onBrowseRepository,
  buildpacksAvailable,
}: NoAppDetectedNoticeProps) => (
    // On a phone the three parts are a column: side by side, the button and the
    // icon left the sentence about 150px to wrap in, and a paragraph broken over
    // eight two-word lines is not a paragraph. `sm:contents` dissolves the
    // wrapper from `sm` up, so the wide layout is the row it always was.
    <div className="flex flex-col items-start gap-3 text-ds-135 leading-[1.55] sm:flex-row sm:items-center sm:gap-3.5">
      <div className="flex items-start gap-3.5 min-w-0 self-stretch sm:contents">
        <span className="w-[30px] h-[30px] flex-none rounded-full bg-state-err/[0.14] text-state-err flex items-center justify-center">
          <AlertIcon width="16" height="16" aria-hidden="true" />
        </span>
        {/* Wide enough not to bind at the form's usual width — it is a ceiling for
            wide viewports, where an uncapped line runs past where the eye tracks
            reliably. Capped on the text and not on the panel: a narrow box
            floating in a wide form looks like a mistake rather than a choice. */}
        {/* Two lines and a way out. It was a heading, a paragraph and two
            bullets — four blocks for a state that does not block anything, and by
            the second bullet it was explaining the file layout of a repository to
            someone who only wanted to point at a different folder. The manifest
            list stays, because it is what the reader can actually go and check,
            but it joins the sentence instead of standing as its own instruction. */}
        <div className="min-w-0 flex-1">
          <div className="font-semibold">No app detected in "{rootDirectoryPath}".</div>
          <div className="mt-0.5 opacity-90">
            {buildpacksAvailable === false ? (
              'Apps here are built from a Dockerfile, and this folder has none.'
            ) : (
              <>
                {/* The list of stacks is five lines on a phone. */}
                <span className="sm:hidden">No supported stack or Dockerfile in this folder.</span>
                <span className="hidden sm:inline">
                  Back4app builds {SUPPORTED_STACKS_SENTENCE} — none of them turned up here.
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="b4-focus flex-none h-[38px] px-3 rounded-control border border-navy-400 text-ds-125 font-semibold text-white hover:bg-navy-600 transition-colors duration-[120ms]"
        onClick={onBrowseRepository}
      >
        Pick another folder
      </button>
    </div>
);

export default NoAppDetectedNotice;
